import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Timer } from 'lucide-react';
import { ROUND_LENGTHS, getRoundLengthKey, setRoundLengthKey } from '../lib/roundLength';

/**
 * Round-length segmented toggle for the Battle lobby.
 *
 * Persists to localStorage via `roundLength` so the live arena picks up
 * the same clock without route-state plumbing. Broadcasts a change event
 * for any listening component.
 */
export default function RoundLengthToggle() {
  const [key, setKey] = useState(getRoundLengthKey());

  // Sync across tabs / arena→hub navigation.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'wq_round_length' && e.newValue) setKey(e.newValue); };
    const onCustom = (e) => setKey(e.detail);
    window.addEventListener('storage', onStorage);
    window.addEventListener('wq:round-length-changed', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('wq:round-length-changed', onCustom);
    };
  }, []);

  const pick = (k) => { setKey(k); setRoundLengthKey(k); };

  return (
    <section data-testid="round-length-toggle">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Timer className="w-3 h-3" /> Round length
        </p>
        <p className="text-[10px] uppercase tracking-widest text-accent font-mono">
          {ROUND_LENGTHS.find(r => r.key === key)?.seconds}s
        </p>
      </div>
      <div
        className="flex items-center gap-1 rounded-full bg-black/40 border border-white/10 backdrop-blur-sm p-1"
        role="tablist"
        aria-label="Choose round length"
      >
        {ROUND_LENGTHS.map((r) => {
          const active = r.key === key;
          return (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => pick(r.key)}
              data-testid={`round-length-${r.key}`}
              className={`relative flex-1 px-3 py-2 rounded-full text-[11px] uppercase tracking-widest font-medium transition ${active ? 'text-black' : 'text-white/70 hover:text-white'}`}
            >
              {active && (
                <motion.span
                  layoutId="round-length-pill"
                  className="absolute inset-0 rounded-full bg-accent"
                  transition={{ type: 'spring', damping: 25, stiffness: 320 }}
                />
              )}
              <span className="relative z-10 flex flex-col items-center leading-tight">
                <span>{r.label}</span>
                <span className={`text-[9px] font-mono normal-case ${active ? 'text-black/70' : 'text-white/40'}`}>{r.seconds}s · {r.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
